<template>
  <div>
    <div v-if="state === null">
      <b-button
        variant="primary"
        size="large"
        @click="onExport"
        class="mt-4"
        style="min-width: 12em"
        >Run data export process</b-button
      >
    </div>
    <div
      v-if="state === 'working'"
      class="mt-4 d-flex justify-content-center"
      style="min-width: 12em"
    >
      Processing ...
    </div>
    <div v-if="state === 'ready'">
      <b-button
        variant="success"
        size="large"
        @click="downloadAllFilesAsZip"
        class="mt-4"
        style="min-width: 12em"
        >Download</b-button
      >
    </div>
  </div>
</template>

<script>
//        _
//  |\ | |_) |\/|   o ._   _ _|_  _. | |
//  | \| |   |  |   | | | _>  |_ (_| | |
//  Needs:
//    npm install file-saver
//    npm install jszip

import { saveAs } from "file-saver";
import { trialName } from "@/components/trialConfig.mjs";
const JSZip = require("jszip");

//   _   _      _
//  | | | | ___| |_ __   ___ _ __ ___
//  | |_| |/ _ \ | '_ \ / _ \ '__/ __|
//  |  _  |  __/ | |_) |  __/ |  \__ \
//  |_| |_|\___|_| .__/ \___|_|  |___/
//               |_|

function clean(value) {
  return value.replace(/,/g, "{comma}").replace(/"/g, "{quotes}");
}
function makeLine(arrayOfValues) {
  return arrayOfValues.map(String).map(clean).join(",") + "\n";
}
function symptomIdFromI0(i0) {
  return "symptom_" + String(1 + i0).padStart(3, "0");
}
function durationBetweenKeys(key1, key2) {
  const time1 = new Date(key1).getTime();
  const time2 = new Date(key2).getTime();
  const diffDays = Math.round((time2 - time1) / 86400000);
  return diffDays;
}

export default {
  name: "DataExport",
  props: {
    participantIdArray: {
      type: Array,
      required: true,
    },
    contactRootObj: {
      type: Object,
      required: true,
    },
    statusRootRef: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      state: null,
      trialContactsCsv: "",
      trialDailyCsv: "",
      trialWeeklyYesNoCsv: "",
    };
  },

  methods: {
    async onExport() {
      this.state = "working";
      const ACTIVITY_KEYS = ["activity_001", "activity_002"];
      const MAX_N_SYMPTOMS = 1;
      const POTENTIAL_SYMPTOM_IDS = [...Array(MAX_N_SYMPTOMS).keys()].map(
        symptomIdFromI0
      );

      const participantIds = this.participantIdArray;

      //   _____                 _                       _               _
      //  | ____|_ __ ___  _ __ | |_ _   _    ___  _   _| |_ _ __  _   _| |_ ___
      //  |  _| | '_ ` _ \| '_ \| __| | | |  / _ \| | | | __| '_ \| | | | __/ __|
      //  | |___| | | | | | |_) | |_| |_| | | (_) | |_| | |_| |_) | |_| | |_\__ \
      //  |_____|_| |_| |_| .__/ \__|\__, |  \___/ \__,_|\__| .__/ \__,_|\__|___/
      //                  |_|        |___/                  |_|
      this.trialContactsCsv = `Participant Id,Start date ISO,${ACTIVITY_KEYS.join(
        ","
      )},Number of symptoms,${POTENTIAL_SYMPTOM_IDS.join(",")}\n`;

      this.trialDailyCsv = `Participant Id,Days since start,Date ISO,Symptom YesNo,Symptom frequency,Symptom severity 1-600\n`;
      this.trialWeeklyYesNoCsv = `Participant Id,Window Id,End date ISO,End relative,Activity Id,Symptom YesNo\n`;

      //   _                                                    _
      //  | |    ___   ___  _ __     _____   _____ _ __   _ __ | |_ ___
      //  | |   / _ \ / _ \| '_ \   / _ \ \ / / _ \ '__| | '_ \| __/ __|
      //  | |__| (_) | (_) | |_) | | (_) \ V /  __/ |    | |_) | |_\__ \
      //  |_____\___/ \___/| .__/   \___/ \_/ \___|_|    | .__/ \__|___/
      //                   |_|                           |_|
      const participantPromises = participantIds.map(async (participantId) => {
        //    ____      _     _               _         __            _
        //   / ___| ___| |_  | |__   __ _ ___(_) ___   / _| ___  __ _| |_ ___
        //  | |  _ / _ \ __| | '_ \ / _` / __| |/ __| | |_ / _ \/ _` | __/ __|
        //  | |_| |  __/ |_  | |_) | (_| \__ \ | (__  |  _|  __/ (_| | |_\__ \
        //   \____|\___|\__| |_.__/ \__,_|___/_|\___| |_|  \___|\__,_|\__|___/

        // Get raw data
        const contact = this.contactRootObj[participantId] || {};
        const statusLive = (
          await this.statusRootRef
            .child(participantId)
            .child("live")
            .once("value")
        ).val();

        // Analyse
        const days = (statusLive || {}).days || {};
        const windows = (statusLive || {}).windows || {};
        const windowKeys = Object.keys(windows);
        const nSymptoms = 1;

        //
        //  \    / ._ o _|_  _     _  _  ._ _|_  _.  _ _|_
        //   \/\/  |  |  |_ (/_   (_ (_) | | |_ (_| (_  |_

        const trialId = contact.trialId || "No trial Id for " + participantId;
        const startDateKey = contact.startDateKey;

        // Each patient has THE SAME number of activities
        const activityStrings = ACTIVITY_KEYS.map(
          (activityKey) =>
            (((contact || {}).windows || {}).questions || {})[activityKey] ||
            "missing activity description"
        );

        // // Each patient has a VARIABLE number of symptoms
        const symptomStringsOrDash = POTENTIAL_SYMPTOM_IDS.map((_) => "-");
        symptomStringsOrDash[0] = contact.myAnginaIs;

        const contactsLine = makeLine([
          trialId,
          startDateKey,
          ...activityStrings,
          nSymptoms,
          ...symptomStringsOrDash,
        ]);
        this.trialContactsCsv += contactsLine;

        //
        //   \    / ._ o _|_  _     _|  _. o |
        //    \/\/  |  |  |_ (/_   (_| (_| | | \/
        //                                     /
        const dateKeys = Object.keys(days);
        dateKeys.forEach((dateKey) => {
          // symptomIds.forEach((symptomId) => {
          const day = (days || {})[dateKey];
          if (day) {
            const iDay = durationBetweenKeys(startDateKey, dateKey);
            const dayLine = makeLine([
              trialId,
              iDay,
              dateKey,
              (day || {}).anginaYesNo || "missing data",
              (day || {}).anginaCount || 0,
              (day || {}).anginaSeverity || 0,
            ]);
            this.trialDailyCsv += dayLine;
          }
          // });
        });

        //
        //  \    / ._ o _|_  _         o ._   _|  _        _
        //   \/\/  |  |  |_ (/_   \/\/ | | | (_| (_) \/\/ _>
        // which has (a) Yes/No BY SYMPTOM & BY ACTIVITY
        // and (b) Restriction & severity, just once per window

        windowKeys.forEach((windowKey) => {
          const windowEndDateKey = (
            (((contact || {}).windows || {}).dates || {})[windowKey] || {}
          ).endDateKey;
          const iEndDate = durationBetweenKeys(startDateKey, windowEndDateKey);
          const windowData = (windows || {})[windowKey];
          if (windowData) {
            // SymptomYesNo
            ACTIVITY_KEYS.forEach((activityKey) => {
              const activity = ((windowData || {}).questions || {})[ // eslint-disable-line standard/computed-property-even-spacing
                activityKey
              ];
              // symptomIds.forEach((symptomId) => {
              const activityLine = makeLine([
                trialId,
                windowKey,
                windowEndDateKey,
                iEndDate,
                activityKey,
                // symptomId,
                (activity || {}).anginaYesNo || "no data",
              ]);
              this.trialWeeklyYesNoCsv += activityLine;
            });
            // });
          }
        });
      });

      await Promise.all(participantPromises);

      setTimeout(() => {
        // Just so people realise something seriously worthwhile is happening
        this.state = "ready";
      }, 5000);
    },

    async downloadAllFilesAsZip() {
      // https://muhimasri.com/blogs/how-to-save-files-in-javascript/

      const zip = new JSZip();
      zip.file("Participants.csv", this.trialContactsCsv);
      zip.file("Daily.csv", this.trialDailyCsv);
      zip.file("Weekly.csv", this.trialWeeklyYesNoCsv);
      const content = await zip.generateAsync({ type: "blob" });
      const zipFileName =
        trialName + " data " + new Date().toISOString().slice(0, 10) + ".zip";
      saveAs(content, zipFileName);
    },
  },
};
</script>

<style scoped lang="scss">
</style>


// WEBPACK FOOTER //
// src/components/DataExport.vue